import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import useCheckoutState from '../hooks/useCheckoutState';
import useIsomorphicLayoutEffect from '../hooks/useIsomorphicLayoutEffect';
import resolveInheritedEnabled from '../lib/resolveInheritedEnabled';
import Badge from './Badge';
import { formatPrice } from '../services/formatPrice';
import { Select, TextInput } from './Form';
import getVisibleTicketTypes from '../lib/getVisibleTicketTypes';
import { getImageSrcAndSrcSet } from '../services/getUrlFromCdn';
import StyledElement from '@repo/checkout/components/StyledElement';
import TicketTypeSaleDate from './TicketTypeSaleDate';
import { shouldShowTicketSaleDates } from '../lib/ticketAvailabilityHelpers';
import checkIsTicketTypeNotOnSale from '../lib/checkIsTicketTypeNotOnSale';
import checkIsTicketGroupNotOnSale from '../lib/checkIsTicketGroupNotOnSale';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import getTicketTypePricing from '../lib/getTicketTypePricing';
import getResolvedTicketPrice from '../lib/getResolvedTicketPrice';
import useCheckoutTicketFormHelpers from '../hooks/useCheckoutTicketFormHelpers';
import getTicketPwywPlaceholder from '../lib/getTicketPwywPlaceholder';
import getTicketFeeAmount from '../lib/getTicketFeeAmount';
import { getTicketCoupon, resolveCouponReach, sharesTicketAmountOff } from '@repo/lib';
import { useCouponPurchase, useTicketCouponShare } from '../hooks/useCartCost';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import QuantityButton from './CheckoutForm/components/QuantityButton';
import QuantityField from './CheckoutForm/components/QuantityField';
import { useCheckoutLocalization } from '../hooks/useCheckoutLocalization';
import { translateStepperLabel } from '../lib/quantityStepperLabels';

// Compact control width for the classic list row (list layout + default
// alignment), where controls sit inline to the right of the ticket text.
// When a pay-what-you-want ticket appears anywhere on the page, its wider
// amount input sets the control width and every quantity select matches it so
// they line up; pages without a pwyw ticket keep the compact default
// (min-w-[72px] below). Applied to the wrapper divs (not the <select>) —
// max-w-full on a bare <select> collapses it to min-content in the flex
// column. 160px because the currency suffix eats ~34px of the amount input
// on top of placeholders like "Enter amount" — 140px still truncated them.
const TICKET_SELECTION_CONTROL_WIDTH = 'w-[160px] max-w-full';

// Controls (the quantity select and pwyw amount input) sit inline to the
// right of the ticket text only in the classic list row — list layout with
// default (or legacy 'left') alignment. Every other layout — any grid, or a
// list with center/stack alignment — stacks them under the text at full card
// width, like the price picker's pwyw input.
function checkHasStackedControls(ticketGroup) {
  if (ticketGroup.layout?.type === 'grid') return true;
  const alignment = ticketGroup.layout?.alignment;
  return alignment === 'center' || alignment === 'stack';
}

export default function TicketType({
  ticketType,
  selectedQuantity,
  isSelected,
  hasQuantitySelect,
  onQuantitySelect,
  ticketGroup,
  isSoldOut,
  isCanceled,
  isUnavailable,
  isOnSale,
  isTicketTypeScheduled,
  ticketTypeHasInvalidReference,
  isTicketGroupScheduled,
  ticketGroupHasInvalidReference,
}) {
  const { page, query, coupon, theme, form } = useCheckoutState();
  const couponShare = useTicketCouponShare(ticketType._id);
  const couponPurchase = useCouponPurchase();
  const labels = getThemeLabels(theme);

  const allTicketTypes = page.ticketGroups?.flatMap(group => group.ticketTypes) || [];
  const allTicketGroups = page.ticketGroups || [];

  const visibleTicketTypes = getVisibleTicketTypes({ page, ticketGroup, query });
  const index = visibleTicketTypes.indexOf(ticketType);

  // Page-constant fact (form state never changes it) — memoised so the
  // page-wide visibility sweep doesn't rerun on every form keystroke.
  const pageHasPwyw = useMemo(
    () =>
      (page.ticketGroups || []).some(group =>
        getVisibleTicketTypes({ page, ticketGroup: group, query }).some(t => t.pricing === 'pwyw'),
      ),
    [page, query],
  );

  const hasStackedControls = checkHasStackedControls(ticketGroup);
  const hasStepper =
    theme?.elements?.checkout?.ticketGroups?.ticketTypes?.quantitySelector === 'stepper';

  const showAvailableQuantity =
    !isCanceled &&
    !isSoldOut &&
    !isUnavailable &&
    ticketType.showAvailableQuantity &&
    ticketType.hasCapacity;

  const isScheduled = isTicketTypeScheduled || isTicketGroupScheduled;
  const hasInvalidReference = ticketTypeHasInvalidReference || ticketGroupHasInvalidReference;

  const isTicketGroupNotOnSale = checkIsTicketGroupNotOnSale({
    ticketGroup,
    isCanceled,
    isOnSale: ticketGroup.isOnSale,
    isScheduled: isTicketGroupScheduled,
    hasInvalidReference: ticketGroupHasInvalidReference,
  });

  const isTicketTypeNotOnSale = checkIsTicketTypeNotOnSale({
    ticketType,
    isCanceled,
    isOnSale: ticketType.isOnSale,
    isScheduled: isTicketTypeScheduled,
    hasInvalidReference: ticketTypeHasInvalidReference,
  });

  const isNotOnsale = isTicketTypeNotOnSale || isTicketGroupNotOnSale;

  const showTicketSaleDates = shouldShowTicketSaleDates(
    ticketType,
    ticketGroup,
    isCanceled,
    isSoldOut,
    isUnavailable,
    isOnSale,
  );

  const formTicketType = form.ticketGroups?.[ticketGroup._id]?.find(
    t => t.ticketTypeId === ticketType._id,
  );
  const price = getResolvedTicketPrice(
    ticketType,
    formTicketType?.payWhatYouWantAmount,
    page.eventDetails.currency,
  );
  const quantity = selectedQuantity;
  // Priced as if this ticket is in the cart, so the row shows what buying it takes off.
  const couponReach = resolveCouponReach(coupon, {
    pageId: page._id,
    pageTicketTypeIds: allTicketTypes.map(t => t._id),
    ticketTypeIds: [...(couponPurchase.ticketTypeIds ?? []), ticketType._id],
    bumpProductIds: couponPurchase.bumpProductIds,
  });
  const pricing = getTicketTypePricing({
    ticketType,
    unitPrice: price,
    quantity,
    ticketTypeDiscounts: page.eventDetails.discounts,
    coupon: getTicketCoupon(coupon, couponReach),
    isTicketTypeCoupon: couponReach.primaryScope === 'ticket',
    sharesAmountOff: sharesTicketAmountOff(coupon, couponReach),
    couponShare,
  });
  const ticketTypeTotal = pricing.totalAmount;

  const ticketTypePrice =
    ticketTypeTotal > 0
      ? ticketTypeTotal / quantity
      : ticketTypeTotal <= 0 && quantity > 0
        ? 0
        : ticketType.price;

  // The fee is charged once per ticket on top of the price, less a ticket
  // coupon's share of it. An unselected ticket has no coupon, so it shows the
  // full fee. The line total is what the customer will pay for the selection.
  const ticketFeeAmount =
    quantity > 0 && pricing.fee
      ? pricing.fee.totalAmount / quantity
      : getTicketFeeAmount(ticketType);
  const ticketTypeTotalWithFee = ticketTypeTotal + (pricing.fee?.totalAmount ?? 0);

  return (
    <div
      value={ticketType._id}
      className={clsx(
        'cp_ticket-type-wrapper relative h-full',
        'border-solid [border-width:var(--cp_checkout-ticketGroups-ticketTypes-border-width)]',
        {
          'bg-[var(--cp_checkout-ticketGroups-ticketTypes-background-color)] border-[var(--cp_checkout-ticketGroups-ticketTypes-border-color)]':
            !isSelected,
          'cp_ticket-type-wrapper--selected bg-[var(--cp_checkout-ticketGroups-ticketTypes-background-color-selected)] border-[var(--cp_checkout-ticketGroups-ticketTypes-border-color-selected)] ring-1 ring-[var(--cp_checkout-ticketGroups-ticketTypes-border-color-selected)]':
            isSelected,
          'p-2': ticketGroup.layout?.spacing === 'compact',
          'p-4': ticketGroup.layout?.spacing === 'comfortable' || !ticketGroup.layout?.spacing,
          'px-2': ticketGroup.layout?.type === 'grid' && ticketGroup.layout?.columns > 3,
          'rounded-[var(--cp_checkout-ticketGroups-ticketTypes-border-radius)]':
            ticketGroup.layout?.type === 'grid',
          'rounded-tl-[var(--cp_checkout-ticketGroups-ticketTypes-border-radius)] rounded-tr-[var(--cp_checkout-ticketGroups-ticketTypes-border-radius)]':
            index === 0,
          'rounded-bl-[var(--cp_checkout-ticketGroups-ticketTypes-border-radius)] rounded-br-[var(--cp_checkout-ticketGroups-ticketTypes-border-radius)]':
            index === visibleTicketTypes.length - 1,
          'z-10': isSelected,
        },
      )}
    >
      <div
        className={clsx('cp_ticket-type flex flex-nowrap w-full space-x-2 space-y-2', {
          'text-left':
            ticketGroup.layout?.alignment === 'default' ||
            ticketGroup.layout?.alignment === 'stack',
          'text-center': ticketGroup.layout?.alignment === 'center',
          'flex-col': hasStackedControls,
        })}
      >
        {ticketType.image && <TicketTypeImage ticketType={ticketType} ticketGroup={ticketGroup} />}

        <div className={clsx('w-full')}>
          {ticketGroup.layout?.showTicketTypeName && <TicketTypeName ticketType={ticketType} />}

          <TicketTypePrice
            ticketTypePrice={ticketTypePrice}
            ticketFeeAmount={ticketFeeAmount}
            ticketType={ticketType}
            ticketGroup={ticketGroup}
          />

          {hasQuantitySelect && ticketTypeTotalWithFee > 0 && (
            <TicketTypeTotal
              ticketTypeTotal={ticketTypeTotalWithFee}
              quantity={selectedQuantity}
              ticketGroup={ticketGroup}
            />
          )}

          {showTicketSaleDates && (
            <TicketTypeSaleDate
              ticketGroup={ticketGroup}
              ticketType={ticketType}
              allTicketTypes={allTicketTypes}
              allTicketGroups={allTicketGroups}
            />
          )}

          {showAvailableQuantity && (
            <div
              className={[
                'cp_ticket-type-available-quantity',
                'text-[var(--cp_checkout-ticketGroups-ticketTypes-remaining-color)]',
                '[font-family:var(--cp_checkout-ticketGroups-ticketTypes-remaining-font-family)]',
                '[font-style:var(--cp_checkout-ticketGroups-ticketTypes-remaining-font-style)]',
                '[font-size:var(--cp_checkout-ticketGroups-ticketTypes-remaining-font-size)]',
                '[line-height:var(--cp_checkout-ticketGroups-ticketTypes-remaining-line-height)]',
                '[font-weight:var(--cp_checkout-ticketGroups-ticketTypes-remaining-font-weight)]',
              ].join(' ')}
            >
              {ticketType.remainingCapacity} {labels.ticketsRemainingText}
            </div>
          )}
        </div>

        {(() => {
          // Determine if we should show any badges or quantity select
          const shouldShowContent =
            isSoldOut ||
            hasQuantitySelect ||
            isOnSale ||
            isCanceled ||
            isUnavailable ||
            isScheduled ||
            isNotOnsale ||
            ticketType.pricing === 'pwyw';

          if (!shouldShowContent) return null;

          return (
            <div
              className={clsx('flex flex-col gap-y-2', {
                'w-full': hasStackedControls,
                'ml-auto pl-4': !hasStackedControls && ticketGroup.layout?.alignment === 'left',
              })}
            >
              {isCanceled ? (
                <TicketTypeCanceled />
              ) : page.eventDetails?.status === 'sold_out' ? (
                <TicketTypeSoldOut />
              ) : ticketGroup.availabilityStatus === 'unavailable' ? (
                <TicketTypeUnavailable />
              ) : isTicketGroupNotOnSale ? (
                <TicketTypeNotOnSale />
              ) : isSoldOut ? (
                <TicketTypeSoldOut />
              ) : isUnavailable ? (
                <TicketTypeUnavailable />
              ) : isNotOnsale || hasInvalidReference ? (
                <TicketTypeNotOnSale />
              ) : isOnSale ? null : isScheduled ? (
                <TicketTypeOnSaleSoon />
              ) : null}
              {hasQuantitySelect &&
                !isSoldOut &&
                !isUnavailable &&
                isOnSale &&
                !isCanceled &&
                hasStepper && (
                  <TicketTypeQuantityStepper
                    ticketType={ticketType}
                    selectedQuantity={selectedQuantity}
                    onQuantitySelect={onQuantitySelect}
                    alignment={hasStackedControls ? ticketGroup.layout?.alignment : null}
                  />
                )}
              {hasQuantitySelect &&
                !isSoldOut &&
                !isUnavailable &&
                isOnSale &&
                !isCanceled &&
                !hasStepper && (
                  <TicketTypeQuantitySelect
                    ticketType={ticketType}
                    selectedQuantity={selectedQuantity}
                    onQuantitySelect={onQuantitySelect}
                    wide={pageHasPwyw}
                    fullWidth={hasStackedControls}
                  />
                )}
              {ticketType.pricing === 'pwyw' &&
                !isSoldOut &&
                !isUnavailable &&
                !isCanceled &&
                isOnSale && (
                  <TicketTypePayWhatYouWantInput
                    ticketType={ticketType}
                    ticketGroup={ticketGroup}
                    isSelected={isSelected}
                    hasQuantitySelect={hasQuantitySelect}
                  />
                )}
            </div>
          );
        })()}
      </div>

      <TicketTypeDescription ticketType={ticketType} ticketGroup={ticketGroup} />
    </div>
  );
}

function TicketTypeImage({ ticketType, ticketGroup }) {
  const { isPreview } = useCheckoutState();
  const { width, height } = (() => {
    switch (ticketGroup.layout?.imageSize) {
      case 'small':
        return { width: 64, height: 64 };
      case 'medium':
        return { width: 128, height: 128 };
      case 'large':
      default:
        return { width: 256, height: 256 };
    }
  })();

  const image = getImageSrcAndSrcSet({
    url: ticketType.image?.url,
    width,
    height,
    isPreview,
  });

  return (
    <div
      className={clsx(
        'cp_ticket-type-image-wrapper max-w-full rounded overflow-hidden flex-shrink-0',
        {
          'mx-auto': ticketGroup.layout?.alignment === 'center',
          'w-16 h-16': ticketGroup.layout?.imageSize === 'small',
          'w-32 h-32': ticketGroup.layout?.imageSize === 'medium',
          'w-64 h-64': ticketGroup.layout?.imageSize === 'large',
        },
      )}
    >
      {image && (
        <img
          src={image.src}
          srcSet={image.srcSet}
          className={clsx('cp_ticket-type-image object-cover', {
            'w-16 h-16': ticketGroup.layout?.imageSize === 'small',
            'w-32 h-32': ticketGroup.layout?.imageSize === 'medium',
            'w-64 h-64': ticketGroup.layout?.imageSize === 'large',
          })}
        />
      )}
    </div>
  );
}

function TicketTypeName({ ticketType }) {
  return (
    <StyledElement
      elementPath="checkout.ticketGroups.ticketTypes.name"
      as="div"
      className={clsx(
        'cp_ticket-type-name font-semibold text-[var(--cp_checkout-ticketGroups-ticketTypes-name-color)]',
        '[font-family:var(--cp_checkout-ticketGroups-ticketTypes-name-font-family)]',
        '[font-style:var(--cp_checkout-ticketGroups-ticketTypes-name-font-style)]',
        '[font-size:var(--cp_checkout-ticketGroups-ticketTypes-name-font-size)]',
        '[line-height:var(--cp_checkout-ticketGroups-ticketTypes-name-line-height)]',
        '[font-weight:var(--cp_checkout-ticketGroups-ticketTypes-name-font-weight)]',
      )}
    >
      {ticketType.name}
    </StyledElement>
  );
}

function TicketTypeDescription({ ticketType, ticketGroup }) {
  return (
    <StyledElement
      elementPath="checkout.ticketGroups.ticketTypes.description"
      as="div"
      className={clsx(
        {
          'text-left':
            ticketGroup.layout?.alignment === 'default' ||
            ticketGroup.layout?.alignment === 'stack',
          'text-center': ticketGroup.layout?.alignment === 'center',
        },
        'cp_ticket-type-description mt-2',
        'text-[var(--cp_checkout-ticketGroups-ticketTypes-description-color)]',
        '[font-family:var(--cp_checkout-ticketGroups-ticketTypes-description-font-family)]',
        '[font-style:var(--cp_checkout-ticketGroups-ticketTypes-description-font-style)]',
        '[font-size:var(--cp_checkout-ticketGroups-ticketTypes-description-font-size)]',
        '[line-height:var(--cp_checkout-ticketGroups-ticketTypes-description-line-height)]',
        '[font-weight:var(--cp_checkout-ticketGroups-ticketTypes-description-font-weight)]',
      )}
    >
      {ticketType.description}
    </StyledElement>
  );
}

/**
 * The fee note under a ticket price. Either mode keeps the full amount the
 * customer pays visible before they select the ticket: "included" folds the
 * fee into the shown price ("Includes $3 booking fee"), "separate" leaves the
 * price alone and adds the fee here ("$3 booking fee").
 */
function sentenceCaseMidline(label = '') {
  const text = String(label);
  if (text.length < 2 || text[1] !== text[1].toLowerCase()) return text;
  return text[0].toLowerCase() + text.slice(1);
}

function TicketTypeFeeNote({ feeAmount, includedInPrice }) {
  const { page, theme, currencyLocale } = useCheckoutState();
  const labels = getThemeLabels(theme);
  const { currency } = page.eventDetails;

  if (!feeAmount) return null;

  const formattedFee = formatPrice({ amount: feeAmount, currency, locale: currencyLocale });
  // The label sits mid-sentence here ("Includes $3 booking fee"), so a
  // sentence-case label loses its capital; an acronym like "VAT" keeps it.
  const feeLabel = sentenceCaseMidline(labels.bookingFeeText);

  return (
    <StyledElement
      elementPath="checkout.ticketGroups.ticketTypes.fee"
      as="div"
      className={clsx(
        'cp_ticket-type-fee',
        'text-[var(--cp_checkout-ticketGroups-ticketTypes-fee-color)]',
        '[font-family:var(--cp_checkout-ticketGroups-ticketTypes-fee-font-family)]',
        '[font-style:var(--cp_checkout-ticketGroups-ticketTypes-fee-font-style)]',
        '[font-size:var(--cp_checkout-ticketGroups-ticketTypes-fee-font-size)]',
        '[line-height:var(--cp_checkout-ticketGroups-ticketTypes-fee-line-height)]',
        '[font-weight:var(--cp_checkout-ticketGroups-ticketTypes-fee-font-weight)]',
      )}
    >
      {includedInPrice
        ? `${labels.ticketFeeIncludesText} ${formattedFee} ${feeLabel}`
        : `${formattedFee} ${feeLabel}`}
    </StyledElement>
  );
}

function TicketTypePrice({ ticketTypePrice, ticketFeeAmount, ticketType, ticketGroup }) {
  const { page, theme, currencyLocale } = useCheckoutState();
  const labels = getThemeLabels(theme);
  const { eventDetails } = page;
  const { currency } = eventDetails;

  const showPrice = ticketGroup.layout?.showTicketPrice;
  const showFeeNote = ticketGroup.layout?.showTicketFee !== false;

  const fullFeeAmount = getTicketFeeAmount(ticketType);
  // A free ticket has no price to fold the fee into: it stays "Free" and the
  // fee is noted underneath, whatever the group's display mode.
  const feeIncludedInPrice =
    fullFeeAmount > 0 && ticketType.pricing !== 'free' && ticketGroup.feeDisplay !== 'separate';

  // PWYW tickets carry their pricing info in the amount input's placeholder;
  // no price line is shown under the name. The fee still needs announcing
  // because it lands on top of whatever amount the customer enters.
  if (ticketType.pricing === 'pwyw') {
    return showPrice && showFeeNote && ticketFeeAmount > 0 ? (
      <TicketTypeFeeNote feeAmount={ticketFeeAmount} includedInPrice={false} />
    ) : null;
  }

  // An included fee is part of the shown price, so the strike-through carries
  // the full fee too. Without it a small bulk discount reads as a price rise.
  const shownPrice = ticketTypePrice + (feeIncludedInPrice ? ticketFeeAmount : 0);
  const includedFullFeeAmount = feeIncludedInPrice ? fullFeeAmount : 0;

  // A seller's discountedFromPrice wins; otherwise the list price is struck
  // through whenever a bulk tier or coupon brought the shown price below it.
  const listPrice = ticketType.price + includedFullFeeAmount;
  const discountedFromPrice = ticketType.discountedFromPrice
    ? ticketType.discountedFromPrice + includedFullFeeAmount
    : shownPrice < listPrice
      ? listPrice
      : '';

  if (showPrice) {
    return (
      <>
        <StyledElement
          elementPath="checkout.ticketGroups.ticketTypes.price"
          as="div"
          className={clsx(
            'cp_ticket-type-price-wrapper inline-flex gap-x-1',
            'cp_ticket-type-price text-[var(--cp_checkout-ticketGroups-ticketTypes-price-color)]',
            '[font-family:var(--cp_checkout-ticketGroups-ticketTypes-price-font-family)]',
            '[font-style:var(--cp_checkout-ticketGroups-ticketTypes-price-font-style)]',
            '[font-size:var(--cp_checkout-ticketGroups-ticketTypes-price-font-size)]',
            '[line-height:var(--cp_checkout-ticketGroups-ticketTypes-price-line-height)]',
            '[font-weight:var(--cp_checkout-ticketGroups-ticketTypes-price-font-weight)]',
          )}
        >
          {discountedFromPrice && (
            <StyledElement
              elementPath="checkout.ticketGroups.ticketTypes.discountedFromPrice"
              as="span"
              className={clsx(
                'cp_ticket-type-discounted-from line-through',
                'text-[var(--cp_checkout-ticketGroups-ticketTypes-discountedFromPrice-color)]',
                '[font-family:var(--cp_checkout-ticketGroups-ticketTypes-discountedFromPrice-font-family)]',
                '[font-style:var(--cp_checkout-ticketGroups-ticketTypes-discountedFromPrice-font-style)]',
                '[font-size:var(--cp_checkout-ticketGroups-ticketTypes-discountedFromPrice-font-size)]',
                '[line-height:var(--cp_checkout-ticketGroups-ticketTypes-discountedFromPrice-line-height)]',
                '[font-weight:var(--cp_checkout-ticketGroups-ticketTypes-discountedFromPrice-font-weight)]',
              )}
            >
              {formatPrice({
                amount: discountedFromPrice,
                currency,
                locale: currencyLocale,
              })}
            </StyledElement>
          )}

          <span className="cp_ticket-type-price-amount">
            {shownPrice > 0
              ? formatPrice({
                  amount: shownPrice,
                  currency,
                  locale: currencyLocale,
                })
              : 'Free'}
          </span>
        </StyledElement>

        {showFeeNote && ticketFeeAmount > 0 && (
          <TicketTypeFeeNote feeAmount={ticketFeeAmount} includedInPrice={feeIncludedInPrice} />
        )}
      </>
    );
  }

  return null;
}

const statusBadgeFontClasses = clsx(
  'whitespace-nowrap',
  '[font-family:var(--cp_checkout-statusBadges-font-family)]',
  '[font-style:var(--cp_checkout-statusBadges-font-style)]',
  '[font-size:var(--cp_checkout-statusBadges-font-size)]',
  '[line-height:var(--cp_checkout-statusBadges-line-height)]',
  '[font-weight:var(--cp_checkout-statusBadges-font-weight)]',
);

function TicketTypeSoldOut() {
  const { theme } = useCheckoutState();
  const labels = getThemeLabels(theme);

  return (
    <Badge
      className={clsx(
        'cp_sold-out cp_ticket-type-soldout',
        'text-[var(--cp_checkout-statusBadges-soldOut-color)]',
        'bg-[var(--cp_checkout-statusBadges-soldOut-background-color)]',
        statusBadgeFontClasses,
      )}
    >
      {labels.soldOutText}
    </Badge>
  );
}

function TicketTypeCanceled() {
  const { theme } = useCheckoutState();
  const labels = getThemeLabels(theme);

  return (
    <Badge
      className={clsx(
        'cp_canceled cp_ticket-type-canceled',
        'text-[var(--cp_checkout-statusBadges-canceled-color)]',
        'bg-[var(--cp_checkout-statusBadges-canceled-background-color)]',
        statusBadgeFontClasses,
      )}
    >
      {labels.canceledText}
    </Badge>
  );
}

function TicketTypeUnavailable() {
  const { theme } = useCheckoutState();
  const labels = getThemeLabels(theme);

  return (
    <Badge
      className={clsx(
        'cp_unavailable cp_ticket-type-unavailable',
        'text-[var(--cp_checkout-statusBadges-unavailable-color)]',
        'bg-[var(--cp_checkout-statusBadges-unavailable-background-color)]',
        statusBadgeFontClasses,
      )}
    >
      {labels.unavailableText || 'Unavailable'}
    </Badge>
  );
}

function TicketTypeOnSaleSoon() {
  const { theme } = useCheckoutState();
  const labels = getThemeLabels(theme);

  return (
    <Badge
      className={clsx(
        'cp_on-sale-soon cp_ticket-type-on-sale-soon',
        'text-[var(--cp_checkout-statusBadges-onSaleSoon-color)]',
        'bg-[var(--cp_checkout-statusBadges-onSaleSoon-background-color)]',
        statusBadgeFontClasses,
      )}
    >
      {labels.onSaleSoonText || 'On sale soon'}
    </Badge>
  );
}

function TicketTypeNotOnSale() {
  const { theme } = useCheckoutState();
  const labels = getThemeLabels(theme);

  return (
    <Badge
      className={clsx(
        'cp_not-on-sale cp_ticket-type-not-on-sale',
        'text-[var(--cp_checkout-statusBadges-notOnSale-color)]',
        'bg-[var(--cp_checkout-statusBadges-notOnSale-background-color)]',
        statusBadgeFontClasses,
      )}
    >
      {labels.notOnSaleText || 'Not on sale'}
    </Badge>
  );
}

function TicketTypeTotal({ ticketTypeTotal, quantity, ticketGroup }) {
  const { page, theme, currencyLocale } = useCheckoutState();
  const labels = getThemeLabels(theme);

  const showTotalPrice = ticketGroup.layout?.showTicketTotalPrice;

  if (!showTotalPrice || quantity === 0) return null;

  return (
    <div
      className={clsx(
        'cp_ticket-type-total whitespace-nowrap',
        'text-[var(--cp_checkout-ticketGroups-ticketTypes-total-color)] [font-family:var(--cp_checkout-ticketGroups-ticketTypes-total-font-family)] [font-style:var(--cp_checkout-ticketGroups-ticketTypes-total-font-style)] [font-weight:var(--cp_checkout-ticketGroups-ticketTypes-total-font-weight)] [font-size:var(--cp_checkout-ticketGroups-ticketTypes-total-font-size)] [line-height:var(--cp_checkout-ticketGroups-ticketTypes-total-line-height)]',
      )}
    >
      {labels.ticketTypeTotalText}{' '}
      {formatPrice({
        amount: ticketTypeTotal,
        currency: page.eventDetails.currency,
        locale: currencyLocale,
      })}
    </div>
  );
}

function TicketTypePayWhatYouWantInput({ ticketType, ticketGroup, isSelected, hasQuantitySelect }) {
  const { setTicketType, setTicketTypePayWhatYouWantAmount } = useCheckoutTicketFormHelpers();
  const { page, form, theme, currencyLocale } = useCheckoutState();
  const labels = getThemeLabels(theme);
  const node = useRef();
  const suffixRef = useRef(null);

  const currency = page.eventDetails.currency;

  // Currency-code suffix, like the other PWYW inputs. Tickets are always
  // one-time, so there's no interval branch. Gated by the base Interval /
  // Currency element — tickets have no subsection of their own to inherit from.
  const suffixText = currency ? currency.toUpperCase() : '';
  const showSuffix =
    !!suffixText && resolveInheritedEnabled(theme?.elements, 'checkout.pwyw.suffix');

  const [suffixWidth, setSuffixWidth] = useState(null);
  useIsomorphicLayoutEffect(() => {
    setSuffixWidth(showSuffix && suffixRef.current ? suffixRef.current.offsetWidth : null);
  }, [showSuffix, suffixText]);

  const formTicketType = form.ticketGroups?.[ticketGroup._id]?.find(
    t => t.ticketTypeId === ticketType._id,
  );

  const displayValue =
    formTicketType?.payWhatYouWantAmount != null ? String(formTicketType.payWhatYouWantAmount) : '';

  const showPlaceholder = ticketGroup.layout?.showPwywPlaceholder ?? true;
  const placeholder = !showPlaceholder
    ? ''
    : getTicketPwywPlaceholder({ ticketType, labels, currency, locale: currencyLocale });

  useEffect(() => {
    const handleScroll = () => {
      node.current?.blur();
    };

    const currentNode = node.current;
    if (currentNode) {
      currentNode.addEventListener('wheel', handleScroll, { passive: true });
    }

    return () => {
      if (currentNode) {
        currentNode.removeEventListener('wheel', handleScroll);
      }
    };
  }, []);

  return (
    <div
      className={clsx(
        'cp_ticket-type-pwyw-input relative',
        checkHasStackedControls(ticketGroup) ? 'w-full' : TICKET_SELECTION_CONTROL_WIDTH,
      )}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <TextInput
        innerRef={node}
        className="cp_form-textinput"
        style={
          showSuffix
            ? {
                paddingRight: `calc(var(--cp_pwyw-input-padding-x) + ${suffixWidth ?? 30}px + 0.4em)`,
              }
            : undefined
        }
        id={`pwyw-ticket-${ticketType._id}`}
        name={`pwyw-ticket-${ticketType._id}`}
        type="number"
        step="any"
        min="0"
        placeholder={placeholder}
        value={displayValue}
        onKeyDown={e => {
          if (e.key === 'e' || e.key === 'E' || e.key === '-') {
            e.preventDefault();
          }
        }}
        onFocus={() => {
          // Focusing the input selects the ticket. Only single-selection
          // groups clear the other selected tickets — 'multiple' groups keep
          // them, mirroring the click path in TicketGroup.
          if (!hasQuantitySelect && !isSelected) {
            setTicketType(ticketGroup._id, ticketType._id, 1, {
              exclusive: ticketGroup.ticketSelectionType !== 'multiple',
            });
          }
        }}
        onChange={e => {
          const rawValue = String(e.target.value || '');
          const value = rawValue
            .replace(/[^\d.,]+/g, '')
            .replace(/(\.\d{2})\d+|(\,\d{2})\d+/, '$1$2')
            .replace(/(\d+)([.,])(\d{2}).*/, '$1$2$3');

          setTicketTypePayWhatYouWantAmount(ticketGroup._id, ticketType._id, value);
        }}
      />
      {showSuffix && (
        <span
          ref={suffixRef}
          className="cp_paywhatyouwant-currency pointer-events-none absolute top-1/2 -translate-y-1/2"
        >
          {suffixText}
        </span>
      )}
    </div>
  );
}

function TicketTypeQuantitySelect({
  ticketType,
  selectedQuantity,
  onQuantitySelect,
  wide,
  fullWidth,
}) {
  const minQuantity = ticketType.minQuantity;
  const maxQuantity = ticketType.maxQuantity;

  return (
    <div
      className={clsx(
        'cp_ticket-type-quantity-wrapper',
        fullWidth ? 'w-full' : wide && TICKET_SELECTION_CONTROL_WIDTH,
      )}
    >
      <Select
        className={clsx(
          'cp_ticket-type-quantity-select cp_form-select',
          !wide && !fullWidth && 'min-w-[72px]',
        )}
        id={ticketType._id}
        name={ticketType._id}
        value={selectedQuantity}
        onChange={event => {
          onQuantitySelect(Number(event.target.value));
        }}
        required={false}
      >
        {minQuantity > 0 && <option value={0}>0</option>}

        {Array.from(
          {
            length: maxQuantity - minQuantity + 1,
          },
          (_, i) => minQuantity + i,
        ).map(quantity => (
          <option key={quantity} value={quantity}>
            {quantity}
          </option>
        ))}
      </Select>
    </div>
  );
}

// The − / + alternative to the dropdown, built from the order bump's stepper
// parts. It keeps the dropdown's range: 0, then min..max. A typed count is
// held as a draft and snaps into that range on blur.
function TicketTypeQuantityStepper({ ticketType, selectedQuantity, onQuantitySelect, alignment }) {
  const { locale } = useCheckoutLocalization();
  const [draft, setDraft] = useState(null);
  const minQuantity = Math.max(ticketType.minQuantity || 0, 0);
  const maxQuantity = ticketType.maxQuantity;
  const quantity = selectedQuantity || 0;
  const name = ticketType.name;

  // The server caps max to the seats left but not min, so min can exceed max; the
  // dropdown then offers only 0, and so does the stepper.
  const canBuy = minQuantity <= maxQuantity;

  const snap = value => {
    if (!canBuy || !Number.isFinite(value) || value <= 0) return 0;
    if (value < minQuantity) return minQuantity;
    return Math.min(value, maxQuantity);
  };
  const atZero = quantity === 0;
  const atMax = !canBuy || quantity >= maxQuantity;

  const step = direction => {
    if (direction < 0) {
      if (atZero) return;
      onQuantitySelect(quantity <= minQuantity ? 0 : quantity - 1);
    } else {
      if (atMax) return;
      onQuantitySelect(quantity === 0 && minQuantity > 1 ? minQuantity : quantity + 1);
    }
  };

  const commit = () => {
    if (draft === null) return;
    const next = snap(draft === '' ? 0 : draft);
    setDraft(null);
    if (next !== quantity) onQuantitySelect(next);
  };

  return (
    <div
      className={clsx(
        'cp_ticket-type-quantity-wrapper cp_ticket-type-quantity-stepper flex shrink-0 items-stretch',
        { 'justify-center': alignment === 'center' },
      )}
    >
      <QuantityButton
        className="cp_ticket-type-quantity-button-minus aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
        onClick={() => step(-1)}
        aria-disabled={atZero || undefined}
        aria-label={translateStepperLabel(locale, 'decrease', name)}
      >
        <MinusIcon className="cp_quantity-button-icon w-5 h-5" />
      </QuantityButton>
      <QuantityField
        className="cp_ticket-type-quantity-input shrink-0"
        fixedWidth
        id={ticketType._id}
        name={ticketType._id}
        value={draft ?? quantity}
        onChange={setDraft}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        min={0}
        max={maxQuantity}
        inputMode="numeric"
        aria-label={translateStepperLabel(locale, 'quantity', name)}
      />
      <QuantityButton
        className="cp_ticket-type-quantity-button-plus aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
        onClick={() => step(1)}
        aria-disabled={atMax || undefined}
        aria-label={translateStepperLabel(locale, 'increase', name)}
      >
        <PlusIcon className="cp_quantity-button-icon w-5 h-5" />
      </QuantityButton>
    </div>
  );
}
